import React, {ReactNode, useEffect, useState} from 'react';
import {Box, Grid, Typography} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PaymentForm from '../../components/PaymentForm';
import { useUserContext } from '../../hooks/auth/UserProvider';
import { checkUserEnabled } from '../../helpers/authorizedUser';
import { ILastBuyTransaction } from '../../models/buy/Buy';
import { lastBuyTransactionsRequest } from '../../hooks/actions/buy';
import { useFidelityLevel } from '../../hooks/auth/FidelityLevel';
import { GREEN_MAIN } from '../../theme/colors';
import FidelityLevelSelector from '../../components/FidelityLevelSelector';

interface DefaultFidelityPageProps{
    titleType: number;
    children: ReactNode;
}

const DefaultFidelityPage: React.FC<DefaultFidelityPageProps> = ({children, titleType}) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [loading, setLoading] = React.useState(true);
    const [lastBuyTransactions, setLastBuyTransactions] = React.useState<ILastBuyTransaction[] | null>(null)
    const {checkIsAuth} = useUserContext();
    const [isPrivateUser, setIsPrivateUser] = useState<boolean>(false);
    // utente autorizzato
    const [isEnabled, setIsEnabled] = React.useState(false)
    const {user, auth} = useUserContext();
    const {fidelityLevel, fetchFidelityLevel} = useFidelityLevel();
    const [title, setTitle] = useState<string>("");
    const [subtitle, setSubTitle] = useState<string|null>(null);


    useEffect(() => {
        if (auth.isLoading) return;
    
        // Se non autenticato → login con redirect alla pagina corrente
        if (!auth.isAuthenticated) {
            auth.signinRedirect({ state: window.location.pathname });
            return;
        }
    
        // Se autenticato ma utente non abilitato → vai alla home
        if (auth.user && user && !checkUserEnabled(user)) {
            navigate('/');
            return;
        }
    
        // Se user presente, gestisci logica utente
        if (user) {
            // PRIVATE USER
            setIsPrivateUser(user.userType === "Private");
    
            // Fetch livello fedeltà
            fetchFidelityLevel().finally();
        }
    
        // Abilita vista e rimuovi loading
        setIsEnabled(true);
        setLoading(false);
    
        // Verifica auth finale (opzionale, non blocca nulla)
        checkIsAuth().finally();
    
    }, [auth.isLoading, auth.isAuthenticated, auth.user, user, navigate]);
    
    // fine utente autorizzato

    useEffect(()=>{
            switch(titleType){
              case 0:
                setTitle("Guadagna Punti fedeltà e bonus");
                setSubTitle(null);
                break;
              case 1:
                setTitle("Presenta il Circuito");
                setSubTitle(null);
                break;
            case 2:
                setTitle("Archivio Punti fedeltà e bonus");
                setSubTitle(null);
                break;
              default:
                setTitle("");
                setSubTitle(null);
                break;
            }
    },[title, children])

    return(
        <>
            <Box sx={{
                width:'100%', 
                display:'flex', 
                flexDirection:'column',
                color: GREEN_MAIN,
                fontFamily:'poppins'
            }}>
                {
                    auth.isAuthenticated && isEnabled &&
                    <>
                        <Grid container justifyContent='center' paddingBottom={6}>
                            <Grid item xs={12} sm={10} md={7}>
                                <Typography
                                    variant="h3"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        mb: 0,
                                        mt: 0,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {t(title)}
                                </Typography>
                                {subtitle && <Typography
                                    variant="h5"
                                    gutterBottom
                                    sx={{
                                        mb: 0,
                                        mt: 0,
                                    }}
                                >
                                    {t(subtitle)}
                                </Typography>}
                            </Grid>
                        </Grid>

                        {/** CHILDREN */}
                        <Grid container justifyContent='center' paddingBottom={5}>
                            <Grid item xs={12} sm={10} md={7}>
                                {children}
                            </Grid>
                        </Grid>
                        
                        {/** FIDELITY */}
                        <FidelityLevelSelector 
                            gender={fidelityLevel.gender} 
                            level={fidelityLevel.level} 
                        />
                    </>
                }
            </Box>
        </>
    )
}

export default DefaultFidelityPage;